import { useState, useRef, useEffect, useContext } from 'react';
import { Product, Tab } from '@/types';
import { useOrganization } from '@/hooks/useOrganization';
import { useHasRole } from '@/hooks/useHasRole';
import { useAuth } from '@/hooks/useAuth';
import { useInvoice } from '@/hooks/useInvoice';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getDiscountValidation } from '@/lib/discountValidation';
import { useAddItemToTab } from './useAddItemToTab';
import { buildAddItemVariables } from '@/lib/offlineMutations';
import { useUpdateTabItem } from './useUpdateTabItem';
import { useTabsUIStore } from '@/stores/tabsUIStore';
import { toast as sonnerToast } from 'sonner';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { WEIGHT_EPS } from '@/utils/weightPricing';

export const useTabItemActions = () => {
  const { currentOrganization } = useOrganization();
  const { currentRole } = useHasRole();
  const { profile, simpleUser } = useAuth();
  const { generateDispatchCancellation, generateDispatchOrder } = useInvoice();
  const { isEnabled } = useFeatureFlag();
  const createKitchenTickets = isEnabled(FEATURE_FLAGS.KITCHEN_VIEW);
  const addItemMutation = useAddItemToTab(currentOrganization);
  const updateItemMutation = useUpdateTabItem(currentOrganization);
  const { translator } = useContext(LanguageProviderContext);

  const [addingProductId, setAddingProductId] = useState<string | null>(null);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);
  const [updatingAction, setUpdatingAction] = useState<string | null>(null);
  const quantityUpdateTimeout = useRef<NodeJS.Timeout | null>(null);
  const productAddTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (quantityUpdateTimeout.current) {
        clearTimeout(quantityUpdateTimeout.current);
      }
      if (productAddTimeout.current) {
        clearTimeout(productAddTimeout.current);
      }
    };
  }, []);

  const handleAddProduct = (tab: Tab | null, product: Product) => {
    if (!tab || addingProductId === product.id) return;

    setAddingProductId(product.id);
    // Fire-and-forget: the optimistic update lands synchronously and the
    // mutation pauses offline instead of blocking the UI.
    addItemMutation.mutate({
      ...buildAddItemVariables(tab.id, currentOrganization?.id ?? '', product),
      createKitchenTickets,
    });

    if (productAddTimeout.current) {
      clearTimeout(productAddTimeout.current);
    }
    productAddTimeout.current = setTimeout(() => {
      setAddingProductId(null);
    }, 300);
  };

  const updateItemQuantity = (itemId: string, quantity: number, currentTab: Tab | null) => {
    if (updatingItemId === itemId) return;

    const currentItem = currentTab?.items.find(item => item.id === itemId);
    if (!currentItem || quantity === currentItem.quantity) return;

    setUpdatingItemId(itemId);
    setUpdatingAction(
      quantity > currentItem.quantity ? 'increase'
        : quantity < currentItem.quantity ? 'decrease'
          : 'remove'
    );

    updateItemMutation.mutate({ itemId, quantity });

    if (currentTab) {
      const serverName = currentTab.serverName || simpleUser?.username || profile?.full_name || undefined;

      if (quantity < currentItem.quantity) {
        const cancelledQuantity = currentItem.quantity - quantity;
        void generateDispatchCancellation({
          id: currentTab.id,
          customerName: currentTab.customerName,
          items: [{ ...currentItem, quantity: cancelledQuantity }],
          createdAt: new Date(),
          tableId: currentTab.tableId,
          serverName,
        }).catch((dispatchError) => {
          console.error('Error generating dispatch cancellation:', dispatchError);
          sonnerToast.warning(translator('Tabs.dispatchCancelFailed'), {
            description: translator('Tabs.quantityUpdatedButPrintFailed'),
            duration: 4000,
          });
        });
      } else if (quantity > currentItem.quantity) {
        const addedQuantity = quantity - currentItem.quantity;
        void generateDispatchOrder({
          id: currentTab.id,
          customerName: currentTab.customerName,
          items: [{ ...currentItem, quantity: addedQuantity }],
          createdAt: new Date(),
          tableId: currentTab.tableId,
          serverName,
        }).catch((dispatchError) => {
          console.error('Error generating dispatch order:', dispatchError);
          sonnerToast.warning(translator('Tabs.dispatchOrderFailed'), {
            description: translator('Tabs.quantityUpdatedButPrintFailed'),
            duration: 4000,
          });
        });
      }
    }

    if (quantityUpdateTimeout.current) {
      clearTimeout(quantityUpdateTimeout.current);
    }
    quantityUpdateTimeout.current = setTimeout(() => {
      setUpdatingItemId(null);
      setUpdatingAction(null);
    }, 300);
  };

  const updateItemWeight = (itemId: string, weightKg: number, currentTab: Tab | null) => {
    if (updatingItemId === itemId) return;

    const currentItem = currentTab?.items.find((item) => item.id === itemId);
    if (!currentItem) return;

    const prevWeight = currentItem.weightKg ?? 0;
    if (weightKg > 0 && Math.abs(prevWeight - weightKg) < WEIGHT_EPS) return;

    setUpdatingItemId(itemId);
    setUpdatingAction(weightKg <= 0 ? 'remove' : 'increase');
    updateItemMutation.mutate({ itemId, weightKg });

    if (quantityUpdateTimeout.current) {
      clearTimeout(quantityUpdateTimeout.current);
    }
    quantityUpdateTimeout.current = setTimeout(() => {
      setUpdatingItemId(null);
      setUpdatingAction(null);
    }, 300);
  };

  const updateItemNotes = (itemId: string, notes: string | null) => {
    updateItemMutation.mutate({ itemId, notes });
  };

  const applyItemDiscount = (itemId: string) => {
    const { itemDiscountInputs, setItemDiscountInputs } = useTabsUIStore.getState();
    const discountValue = parseFloat(itemDiscountInputs[itemId]) || 0;

    const { validation } = getDiscountValidation(discountValue, currentRole);
    if (validation.isValid) {
      updateItemMutation.mutate({
        itemId,
        discount: Math.max(0, Math.min(100, discountValue)),
      });
      setItemDiscountInputs((prev: Record<string, string>) => ({ ...prev, [itemId]: '' }));
    }
  };

  const cleanupProductAddition = () => {
    if (productAddTimeout.current) {
      clearTimeout(productAddTimeout.current);
      setAddingProductId(null);
    }
  };

  const cleanupQuantityUpdate = () => {
    if (quantityUpdateTimeout.current) {
      clearTimeout(quantityUpdateTimeout.current);
      setUpdatingItemId(null);
      setUpdatingAction(null);
    }
  };

  return {
    handleAddProduct,
    updateItemNotes,
    applyItemDiscount,
    updateItemQuantity,
    updateItemWeight,
    addingProductId,
    updatingItemId,
    updatingAction,
    cleanupProductAddition,
    cleanupQuantityUpdate,
  };
};
